import type { QuoteSummary } from '@/hooks/useQuotes';
import { formatDollars } from '@/lib/format';

const CADENCE: Record<string, string> = { Day: '/day', Week: '/wk', Month: '/mo', Year: '/yr' };

export interface QuoteHeadline {
  amount: number;
  cadence: string;
  introNote: string | null;
}

export type HeadlineSource = Pick<
  QuoteSummary, 'total' | 'steadyRecurringSubtotal' | 'billingFrequency' | 'cancelOnly' | 'cancelledMonthly'
>;

// quote.total is the first invoice, so a ramp with a free intro month totals $0. Lead with the
// settled recurring rate and keep the first invoice as a note. A quote that only cancels invoices
// nothing: its headline is the recurring revenue that leaves.
export function quoteHeadline(quote: HeadlineSource): QuoteHeadline {
  if (quote.cancelOnly && quote.cancelledMonthly != null) {
    return { amount: -quote.cancelledMonthly, cadence: '/mo', introNote: null };
  }
  const steady = quote.steadyRecurringSubtotal;
  if (steady == null) return { amount: quote.total, cadence: '', introNote: null };
  return {
    amount: steady,
    cadence: CADENCE[quote.billingFrequency ?? ''] ?? '',
    introNote: steady === quote.total ? null : `First invoice ${formatDollars(quote.total)}`,
  };
}

export function formatQuoteHeadline(quote: HeadlineSource): string {
  const headline = quoteHeadline(quote);
  const amount = `${formatDollars(headline.amount)}${headline.cadence}`;
  return headline.introNote ? `${amount} (${headline.introNote.toLowerCase()})` : amount;
}
